import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy as CopyIcon, Reply as ReplyIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../../components/ui/sheet/index.js";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";
import { cn } from "../../lib/utils.js";

const LONG_PRESS_MS = 450;

export function useMessageActions(input: {
  reply: boolean;
  copyText: string | null;
  onReply?: () => void;
}): {
  gestures: React.HTMLAttributes<HTMLLIElement>;
  toolbar: React.JSX.Element | null;
} {
  const { t } = useTranslation();
  const notifications = useOptionalNotifications();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const available = input.reply || input.copyText != null;

  function clearTimer(): void {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }

  async function copy(): Promise<void> {
    if (!input.copyText) return;
    try {
      await navigator.clipboard.writeText(input.copyText);
      notifications?.pushNotification({ message: t("inbox.messageActions.copied") });
    } catch {
      notifications?.pushNotification({ message: t("inbox.messageActions.copyFailed") });
    }
  }

  function runReply(): void {
    setSheetOpen(false);
    input.onReply?.();
  }

  const gestures: React.HTMLAttributes<HTMLLIElement> = available
    ? {
        onPointerDown: (event) => {
          if (event.pointerType === "mouse") return;
          startRef.current = { x: event.clientX, y: event.clientY };
          timerRef.current = window.setTimeout(() => setSheetOpen(true), LONG_PRESS_MS);
        },
        onPointerMove: (event) => {
          const start = startRef.current;
          if (!start) return;
          if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) clearTimer();
        },
        onPointerUp: clearTimer,
        onPointerCancel: clearTimer,
        onContextMenu: (event) => {
          if (!window.matchMedia("(max-width: 767px)").matches) return;
          event.preventDefault();
          setSheetOpen(true);
        },
      }
    : {};

  if (!available) return { gestures, toolbar: null };

  const toolbar = (
    <>
      <div
        role="toolbar"
        aria-label={t("inbox.messageActions.toolbar")}
        data-testid="message-actions"
        className={cn(
          "max-md:hidden flex gap-0.5 self-center rounded-[9px] border border-inbox-line bg-white p-0.5 opacity-0 shadow-[0_4px_12px_rgb(16_58_50/0.08)] transition-opacity duration-150 pointer-events-none group-hover/bubble:pointer-events-auto group-hover/bubble:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100 motion-reduce:transition-none",
          hidden && "!opacity-0",
        )}
        onKeyDown={(event) => {
          if (event.key === "Escape") setHidden(true);
        }}
        onMouseLeave={() => setHidden(false)}
      >
        {input.reply ? (
          <ActionButton label={t("inbox.messageActions.reply")} onClick={runReply}>
            <ReplyIcon size={16} className="rtl:-scale-x-100" aria-hidden="true" />
          </ActionButton>
        ) : null}
        {input.copyText != null ? (
          <ActionButton label={t("inbox.messageActions.copy")} onClick={() => void copy()}>
            <CopyIcon size={16} aria-hidden="true" />
          </ActionButton>
        ) : null}
      </div>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="bg-surface p-3">
          <SheetTitle className="sr-only">{t("inbox.messageActions.toolbar")}</SheetTitle>
          <div className="flex flex-col gap-1">
            {input.reply ? (
              <button type="button" className="min-h-11 rounded-lg px-3 text-start text-[0.9375rem]" onClick={runReply}>
                {t("inbox.messageActions.reply")}
              </button>
            ) : null}
            {input.copyText != null ? (
              <button
                type="button"
                className="min-h-11 rounded-lg px-3 text-start text-[0.9375rem]"
                onClick={() => {
                  setSheetOpen(false);
                  void copy();
                }}
              >
                {t("inbox.messageActions.copy")}
              </button>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );

  return { gestures, toolbar };
}

function ActionButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <SimpleTooltip content={label}>
      <button
        type="button"
        aria-label={label}
        className="grid size-7 place-items-center rounded-[7px] text-inbox-muted hover:bg-inbox-hover hover:text-inbox-strong focus-visible:outline-2 focus-visible:outline-primary"
        onClick={onClick}
      >
        {children}
      </button>
    </SimpleTooltip>
  );
}
